/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { TableFooter, Text, Theme, VFlow } from 'bold-ui'
import { AccordionDataTable } from 'components/accordion/accordion-data-table/AccordionDataTable'
import { DateTime } from 'components/date'
import { TableBox } from 'components/table/TableBox'
import { usePagedTableProps } from 'components/table/usePagedTableProps'
import { useTheme } from 'emotion-theming'
import { EvolucoesOdontoInput, PageInfo } from 'graphql/types.generated'

import { CaracteristicasLesaoPanel } from '../components/caracteristicas-lesao/CaracteristicasLesaoPanel'
import { TecidosMolesFieldModel } from '../TecidosMolesField'
import { HistoricoTecidosMolesTableHeader } from './HistoricoTecidosMolesTableHeader'
import HistoricoTecidosMolesTableRow from './HistoricoTecidosMolesTableRow'

export interface HistoricoTecidosMolesTableRowModel extends TecidosMolesFieldModel {
  data: Date
  profissional: string
}

export type HistoricoTecidosMolesTableModel = {
  content: HistoricoTecidosMolesTableRowModel[]
  pageInfo: PageInfo
}

interface HistoricoTecidosMolesTableProps {
  result: HistoricoTecidosMolesTableModel
  loading: boolean
  filterState: EvolucoesOdontoInput
  onChange(filter: any): void
  onChangeFilter(filter: EvolucoesOdontoInput): void
}

export default function HistoricoTecidosMolesTable(props: HistoricoTecidosMolesTableProps) {
  const { loading, result, filterState, onChange, onChangeFilter } = props

  const theme = useTheme<Theme>()

  const tableFooterProps = usePagedTableProps({
    loading,
    result,
    onChange,
  })

  const renderData = (item: HistoricoTecidosMolesTableRowModel) => <DateTime value={item.data} format='DD/MM/YYYY' />

  const renderProcedimentos = (item: HistoricoTecidosMolesTableRowModel) =>
    !!item.procedimentos?.length ? (
      <VFlow vSpacing={0}>
        {item.procedimentos.map((proced) => (
          <Text key={proced.id}>
            {proced.descricao} - {proced.codigo}
          </Text>
        ))}
      </VFlow>
    ) : (
      <Text>-</Text>
    )

  return (
    <TableBox header={<HistoricoTecidosMolesTableHeader filter={filterState} onChange={onChangeFilter} />}>
      <AccordionDataTable<HistoricoTecidosMolesTableRowModel>
        columns={[
          {
            name: 'data',
            header: 'Data dos procedimentos',
            render: renderData,
            size: 2,
            xl: 1,
          },
          {
            name: 'local',
            header: 'Local',
            render: (item) => <Text>{item.local.label}</Text>,
            size: 2,
          },
          {
            name: 'problemas',
            header: 'Problema/condição',
            render: (item) => <Text>{item.problema ? `${item.problema.nome} - ${item.problema.codigo}` : '-'}</Text>,
            size: 3,
          },
          {
            name: 'procedimentos',
            header: 'Procedimentos',
            render: renderProcedimentos,
            size: 3,
            xl: 4,
          },
          {
            name: 'profissional',
            header: 'Profissional',
            render: (item) => item.profissional,
            size: 2,
          },
        ]}
        rows={tableFooterProps.rows}
        components={{
          AccordionPanel: ({ row }) => <CaracteristicasLesaoPanel item={row.caracteristicasLesao} />,
          Row: HistoricoTecidosMolesTableRow,
        }}
        disableRow={(row) => !row.caracteristicasLesao}
        disableRowMessage='Não foram inseridas características da lesão nesse registro'
      />
      <TableFooter
        {...tableFooterProps}
        pageSize={filterState.pageParams?.size}
        sizeOptions={[5, 15, 25, 50]}
        style={css`
          border-top: 0;
          background-color: ${theme.pallete.surface.main};
        `}
      />
    </TableBox>
  )
}
